import { Button, Card, CardContent, CardFooter, Input } from '@ibsys/kit';
import { PlusIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PredicateInput } from '@/features/PredicateInput';
import { Icon } from '@/shared/ui';
import { NEW_ID, StopWordFormValues } from './types';

type Props = {
  index: number;
  onRemove: () => void;
  onAdd?: () => void;
  onSave?: () => void;
  isLast: boolean;
  canAddNew: boolean;
  isStopWordDirty: boolean;
};

export const StopWordItem: FC<Props> = (props) => {
  const { index, onRemove, onAdd, onSave, isLast, canAddNew, isStopWordDirty } = props;

  const { t } = useTranslation('stopWords');
  const { control, watch, handleSubmit } = useFormContext<StopWordFormValues>();
  const name = `stopWords.${index}` as const;

  const stopWordValue = watch(name);
  const stopWordId = stopWordValue?.id;

  const allowRemove = stopWordId !== NEW_ID || (stopWordId === NEW_ID && !isLast);

  const hint = useMemo(() => {
    if (!isLast) {
      return '';
    }

    return t('shiftEnterAdd');
  }, [isLast, t]);

  const handleAddClick = useCallback(() => {
    if (canAddNew) {
      onAdd?.();
    }
  }, [canAddNew, onAdd]);

  const handleBlur = handleSubmit(() => {
    if (isStopWordDirty) {
      // onSave?.();
    }
  });

  return (
    <Card>
      <CardContent>
        <form className="flex flex-col gap-4 @container/word" onBlur={handleBlur} onSubmit={(e) => e.preventDefault()}>
          <Input
            control={control}
            name={`${name}.name`}
            className="w-full @md/word:max-w-1/2"
            placeholder={t('namePlaceholder')}
          />
          <PredicateInput
            control={control}
            name={`${name}.predicate`}
            onChange={onSave}
            classNames={{ input: 'min-h-20' }}
          />
        </form>
      </CardContent>

      <CardFooter className="border-border border-t flex flex-wrap justify-between items-center gap-2">
        <div className="flex gap-2 items-center">
          {allowRemove && (
            <Button
              icon={<Icon type="trash" height={16} width={16} />}
              variant="ghost"
              className="size-7"
              onClick={onRemove}
              color="destructive"
            />
          )}

          {!!hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
        {isLast && (
          <Button variant="outline" onClick={handleAddClick} icon={<PlusIcon />} size="sm" disabled={!canAddNew}>
            {t('addStopWord')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
